import { useListContext, useCreatePath, useNotify } from 'ra-core';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageSpinner } from '@/components/ui/page-spinner';
import { AlertCircle, Car, CheckCircle, Clock, Gavel, House } from 'lucide-react';
import { saleStatusBadge } from '@/lib/saleStatus';
import { formatBRL as formatCurrency, formatBrDateLocal, formatBrDateTime } from '@/lib/format';
import { useState } from 'react';
import { LanceStatusBadge, type LanceStatus } from '../../misc/LanceStatusBadge';
import { PixModal } from './PixModal';
import { Etiqueta, Selo, type TomSelo } from './pecasPremium';
import { useMascara } from '../../valores-ocultos/contexto';
import { rotuloSegmento, segmentoDaVenda, type Segmento } from '../segmentoVenda';
import { GrupoLink } from '../../consortium-groups/GrupoLink';
import type { EstrategiaContemplacao } from '../../commons/estrategiaContemplacao';

type ApiResponse = {
  success?: boolean;
  mock?: boolean;
  contract_url?: string;
  boleto_url?: string | null;
  pix_key?: string | null;
  qr_code?: string | null;
  link_url?: string | null;
  [key: string]: any;
};

export type Sale = {
  id: string;
  venda_data: string;
  cota: string;
  contrato: string;
  group_number: string;
  has_insurance: boolean;
  nome_consorciado: string;
  valor_parcela: number;
  valor_credito: number;
  is_flex: boolean | null;
  pct_flex: number | null;
  vencimento: string;
  payment_method: string;
  status: 'started' | 'pending' | 'failed' | 'success' | 'reconciled' | 'confirmed' | 'cancelled';
  created_at: string;
  contact_id: number;
  cpf: string;
  email: string;
  vendedor: string;
  vendedor_email: string;
  hours_pending: number;
  api_response?: ApiResponse;
  next_assembleia_date: string | null;
  next_assembleia_time: string | null;
  next_lance_status: LanceStatus;
  next_lance_tipo: 'F' | 'L' | 'D' | null;
  next_lance_percentual: number | null;
  next_lance_valor: number | null;
  next_lance_error: string | null;
  created_via: 'north_sell' | 'servopa_sync' | null;
  lance_fixo_automatico: boolean;
  // Estratégia de contemplação (migration 237). Opcionais: o perfil de leitura
  // e telas que não pedem a coluna não trazem.
  estrategia_contemplacao?: EstrategiaContemplacao | null;
  estrategia_alterada_em?: string | null;
  estrategia_alterada_por?: string | null;
  estrategia_alterada_por_nome?: string | null;
  // Estado de VENDA (da base de produção / inadimplentes), exposto pela view.
  contemplado: boolean | null;
  data_contemplacao: string | null;
  is_inadimplente: boolean | null;
  parcelas_pagas: number | null;
  // Só p/ status='cancelled': 'churn' = era cliente pagando e parou;
  // 'nao_confirmada' = nunca virou contrato pagante.
  cancel_kind: 'churn' | 'nao_confirmada' | null;
};

// Visuals per canonical status kind; the label comes from saleStatusBadge so
// the list shows the same three user-facing states as the venda detail views.
const statusConfig = {
  confirmed: { icon: CheckCircle, color: 'text-green-700' },
  pending: { icon: Clock, color: 'text-amber-600' },
  cancelled: { icon: AlertCircle, color: 'text-red-700' },
} as const;


const formatDate = (date: string) => {
  if (!date) return '-';

  // Date-only values (YYYY-MM-DD) should be rendered as local calendar dates,
  // avoiding UTC parsing that can shift one day backward on some timezones.
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
  }

  return formatBrDateLocal(date);
};

interface SaleListProps {
  /**
   * Grupo → segmento, carregado uma vez pela página. Vem de fora porque o
   * segmento não está na venda: quem sabe é `servopa_groups`, e buscar isso
   * por card seria uma consulta por linha da lista.
   */
  segmentoPorGrupo?: Map<string, Segmento>;
}

const SEM_SEGMENTO: Map<string, Segmento> = new Map();

export const SaleList = ({ segmentoPorGrupo = SEM_SEGMENTO }: SaleListProps = {}) => {
  const { data: sales, isPending, error } = useListContext<Sale>();
  const [pixModalOpen, setPixModalOpen] = useState<string | null>(null); // ID da sale com modal aberto

  if (isPending) {
    return <PageSpinner />;
  }

  if (error) {
    return (
      <div className="m-4 rounded-lg bg-erro-fundo p-4 text-sm font-medium text-erro">
        Não foi possível carregar as vendas.
      </div>
    );
  }

  if (!sales || sales.length === 0) {
    return (
      <div className="text-center text-muted-foreground p-8">
        <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>Nenhuma venda registrada</p>
      </div>
    );
  }

  return (
    // Uma venda por linha (redesign Premium North, 19/10/2026). Mesmas
    // informações e ações do cartão de antes; dados que andam juntos dividem a
    // célula para a tabela caber sem rolar de lado. Abaixo de 56rem de largura
    // útil a linha vira cartão de rótulo/valor (container query, não viewport:
    // o menu aberto ou recolhido muda o espaço).
    <div className="@container">
      <div
        role="row"
        className={`hidden border-b border-border px-4 pb-2 pt-1 text-xs font-medium text-muted-foreground @4xl:grid ${COLUNAS}`}
      >
        <span>Cliente</span>
        <span>Contrato · cota</span>
        <span className="text-right">Parcela · crédito</span>
        <span>Datas</span>
        <span>Pagamento</span>
        <span>Vendedor</span>
        <span>Situação</span>
      </div>
      {sales.map((sale) => (
        <LinhaVenda
          key={sale.id}
          sale={sale}
          segmentoPorGrupo={segmentoPorGrupo}
          abrirPix={() => setPixModalOpen(sale.id)}
        />
      ))}

      {/* Modal PIX */}
      {pixModalOpen && (() => {
        const sale = sales.find(s => s.id === pixModalOpen);
        if (!sale) return null;
        const { pixKey, qrCode } = dadosDePagamento(sale);
        return (
          <PixModal
            open={true}
            onClose={() => setPixModalOpen(null)}
            pixKey={pixKey}
            qrCode={qrCode}
          />
        );
      })()}
    </div>
  );
};

/** As sete colunas da tabela larga; a mesma grade no cabeçalho e nas linhas. */
const COLUNAS =
  '@4xl:grid-cols-[minmax(0,2.1fr)_minmax(0,1.5fr)_minmax(0,1.15fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)] @4xl:gap-4';

/** Extrai do retorno da API os dados de pagamento (mesma ordem de fallback de antes). */
const dadosDePagamento = (sale: Sale) => {
  const apiResponse = sale.api_response;
  const normalized = (apiResponse as any)?.normalized || {};
  const raw = (apiResponse as any)?.raw || apiResponse || {};
  const confirmation = raw?.confirmation || {};
  return {
    contractUrl:
      normalized.contractUrl ||
      confirmation.contractLink ||
      confirmation.contract_url ||
      raw?.contract_url ||
      raw?.url_contrato ||
      null,
    boletoUrl:
      normalized.boletoUrl ||
      confirmation.boletoLink ||
      confirmation.boleto_url ||
      raw?.boleto_url ||
      raw?.url_boleto ||
      null,
    pixKey:
      normalized.pixKey ||
      confirmation.qrCode ||
      confirmation.pix_key ||
      raw?.pix_key ||
      raw?.chave_pix ||
      null,
    qrCode:
      normalized.qrCodeData ||
      confirmation.qrImage ||
      confirmation.qr_code ||
      raw?.qr_code ||
      raw?.qrcode ||
      null,
    linkUrl:
      normalized.linkUrl ||
      confirmation.paymentLink ||
      confirmation.payment_link ||
      raw?.payment_link ||
      raw?.url_link_pagamento ||
      null,
  };
};

/** Rótulo pequeno + valor. O rótulo aparece sempre: na tabela larga explica o
 *  segundo dado da célula; no cartão estreito é o rótulo do campo. */
const Campo = ({ rotulo, children, className = '' }: { rotulo: string; children: ReactNode; className?: string }) => (
  <div className={`min-w-0 text-xs leading-snug ${className}`}>
    <span className="text-muted-foreground">{rotulo}</span>{' '}
    <span className="font-medium text-foreground">{children}</span>
  </div>
);

/** Título da célula no cartão estreito; some na tabela larga (o cabeçalho diz). */
const TituloCelula = ({ children }: { children: ReactNode }) => (
  <p className="mb-0.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground @4xl:hidden">
    {children}
  </p>
);

const LinhaVenda = ({
  sale,
  segmentoPorGrupo,
  abrirPix,
}: {
  sale: Sale;
  segmentoPorGrupo: Map<string, Segmento>;
  abrirPix: () => void;
}) => {
  const createPath = useCreatePath();
  const navigate = useNavigate();
  const notify = useNotify();
  const m = useMascara();

  const badge = saleStatusBadge(sale.status, sale.cancel_kind);
  const statusInfo = statusConfig[badge.kind];
  const Icon = statusInfo.icon;
  const pendingHours = Number.isFinite(sale.hours_pending) ? sale.hours_pending : 0;
  const pendingDays = pendingHours / 24;
  const isPendingSale = sale.status === 'pending';
  const daysOverdue = isPendingSale ? Math.floor(pendingDays) : 0;
  const contactPath = createPath({ resource: 'contacts', type: 'show', id: sale.contact_id });
  const segmento = segmentoDaVenda(sale.group_number, segmentoPorGrupo);
  // Cor da faixa lateral, por prioridade (a mesma de antes). Cancelada distingue
  // churn (era cliente que parou de pagar → vermelho) de não-confirmada (nunca
  // virou contrato pagante → cinza, churn esperado de pipeline).
  const stripeColor =
    sale.status === 'cancelled'
      ? (sale.cancel_kind === 'churn' ? 'border-l-red-500' : 'border-l-slate-400')
    : sale.is_inadimplente ? 'border-l-orange-500'
    : sale.contemplado ? 'border-l-[#EFC563]'
    : {
        confirmed: 'border-l-green-500',
        pending: 'border-l-yellow-500',
        cancelled: 'border-l-red-500',
      }[badge.kind];
  // O texto da situação, com a cor do selo pelo mesmo significado da faixa.
  const tomSituacao: TomSelo =
    sale.status === 'cancelled'
      ? (sale.cancel_kind === 'churn' ? 'erro' : 'neutro')
      : badge.kind === 'confirmed' ? 'ok'
      : badge.kind === 'pending' ? 'alerta'
      : 'erro';

  const { contractUrl, boletoUrl, pixKey, qrCode, linkUrl } = dadosDePagamento(sale);
  const paymentMethod = (sale.payment_method || '').toLowerCase();
  const linkPagamento = 'font-medium text-primary underline underline-offset-2 hover:text-marinho cursor-pointer text-left';

  // Renderiza o meio de pagamento mantendo "link de pagamento" como texto puro
  const renderPaymentMethod = () => {
    if (paymentMethod.includes('boleto') && boletoUrl) {
      return (
        <a href={boletoUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className={linkPagamento}>
          {sale.payment_method}
        </a>
      );
    }
    if (paymentMethod.includes('pix') && (pixKey || qrCode)) {
      return (
        <button onClick={(e) => { e.stopPropagation(); abrirPix(); }} className={linkPagamento}>
          {sale.payment_method}
        </button>
      );
    }
    if ((paymentMethod.includes('link') || paymentMethod.includes('cartao') || paymentMethod.includes('pagamento')) && linkUrl) {
      return (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await navigator.clipboard.writeText(linkUrl);
              notify('Link de pagamento copiado para a área de transferência', { type: 'info' });
            } catch {
              notify('Não foi possível copiar o link', { type: 'error' });
            }
          }}
          className={linkPagamento}
          title="Copiar link de pagamento"
        >
          {sale.payment_method}
        </button>
      );
    }
    return <span className="font-medium">{sale.payment_method}</span>;
  };

  return (
    <div
      data-linha-venda
      role="row"
      className={`grid cursor-pointer grid-cols-1 gap-3 border-b border-l-[3px] border-b-border px-4 py-3 transition-colors hover:bg-linha-hover dark:hover:bg-muted/40 @xl:grid-cols-2 ${COLUNAS} ${stripeColor}`}
      // `daLista`: o voltar da venda volta no histórico, pra lista reabrir
      // com os mesmos filtros e no mesmo ponto (VendaShowPage).
      onClick={() => navigate(`/vendas/${sale.id}`, { state: { daLista: true } })}
      title="Ver detalhes da venda"
    >
      {/* Cliente: nome, e-mail, origem, estratégia e o atalho para o contato. */}
      <div className="min-w-0 @xl:col-span-2 @4xl:col-span-1">
        <div className="flex min-w-0 items-start gap-2">
          <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${statusInfo.color}`} />
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-marinho">{sale.nome_consorciado}</h3>
            <p className="break-all text-xs text-muted-foreground">{sale.email || '-'}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {sale.created_via === 'north_sell' && (
                <Etiqueta className="border-[#CFD7E4] bg-[#EEF1F6] text-primary" title="Venda iniciada no North CRM">
                  North
                </Etiqueta>
              )}
              {sale.created_via === 'servopa_sync' && (
                <Etiqueta title="Venda realizada no site da Servopa e sincronizada com o North CRM">Servopa</Etiqueta>
              )}
              {sale.estrategia_contemplacao === 'sorteio' && (
                <Etiqueta title="Estratégia de contemplação: sem lance; a cota concorre apenas no sorteio">
                  Só sorteio
                </Etiqueta>
              )}
              {sale.lance_fixo_automatico === false && sale.estrategia_contemplacao !== 'sorteio' && (
                <Etiqueta
                  className="border-transparent bg-alerta-fundo text-alerta"
                  title="Lance fixo automático desligado: a cota não recebe lance automático no sync diário."
                >
                  <Gavel className="h-2.5 w-2.5" />
                  Sem lance auto
                </Etiqueta>
              )}
              <Link
                to={contactPath}
                className="text-xs font-medium text-primary underline underline-offset-2"
                onClick={(e) => e.stopPropagation()}
              >
                Ver contato
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Contrato, cota, grupo e segmento: o grupo define o segmento. */}
      <div className="min-w-0 space-y-0.5">
        <TituloCelula>Contrato · cota</TituloCelula>
        <Campo rotulo="Contrato">
          {sale.contrato && contractUrl ? (
            <a
              href={contractUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="break-all font-mono font-semibold text-primary underline underline-offset-2 hover:text-marinho"
            >
              {sale.contrato}
            </a>
          ) : (
            <span className="font-mono font-semibold">{sale.contrato || '-'}</span>
          )}
        </Campo>
        <Campo rotulo="Cota">
          <span className="font-mono">{sale.cota}</span>
        </Campo>
        <Campo rotulo="Grupo">
          {sale.group_number ? <GrupoLink groupNumber={sale.group_number} /> : '-'}
        </Campo>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground">Segmento</span>
          <span className="inline-flex items-center gap-1 font-medium">
            {segmento === 'I' && <House className="h-3.5 w-3.5 text-muted-foreground" />}
            {segmento === 'M' && <Car className="h-3.5 w-3.5 text-muted-foreground" />}
            {rotuloSegmento(segmento)}
          </span>
        </div>
      </div>

      {/* Parcela em destaque (como antes), crédito e Flex/Cheia. */}
      <div className="min-w-0 @4xl:text-right">
        <TituloCelula>Parcela · crédito</TituloCelula>
        <div className="text-[15px] font-bold leading-tight tabular-nums text-[#059669]">
          {m(formatCurrency(sale.valor_parcela))}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">Crédito: {m(formatCurrency(sale.valor_credito))}</div>
        {sale.pct_flex != null && (
          <div className="mt-1">
            {sale.is_flex && sale.pct_flex < 100 ? (
              <Etiqueta className="border-transparent bg-alerta-fundo text-alerta">
                Flex {m(`${100 - Number(sale.pct_flex)}%`)}
              </Etiqueta>
            ) : (
              <Etiqueta className="border-transparent bg-muted text-muted-foreground">Cheia</Etiqueta>
            )}
          </div>
        )}
      </div>

      {/* Datas da venda. */}
      <div className="min-w-0 space-y-0.5">
        <TituloCelula>Datas</TituloCelula>
        <Campo rotulo="Venda">{formatDate(sale.venda_data)}</Campo>
        <Campo rotulo="Vencimento">{formatDate(sale.vencimento)}</Campo>
      </div>

      {/* Pagamento (clicável quando há boleto, Pix ou link) e seguro. */}
      <div className="min-w-0 space-y-0.5">
        <TituloCelula>Pagamento</TituloCelula>
        <div className="text-xs">{renderPaymentMethod()}</div>
        <Campo rotulo="Seguro">
          <span className={sale.has_insurance ? 'text-ok' : 'text-muted-foreground'}>
            {sale.has_insurance ? 'Sim' : 'Não'}
          </span>
        </Campo>
      </div>

      <div className="min-w-0">
        <TituloCelula>Vendedor</TituloCelula>
        <p className="break-words text-xs font-medium">{sale.vendedor}</p>
      </div>

      {/* Situação: estado da venda, contemplação, inadimplência, lance, atraso e criação. */}
      <div className="min-w-0 @xl:col-span-2 @4xl:col-span-1">
        <TituloCelula>Situação</TituloCelula>
        <div className="flex flex-wrap items-center gap-1.5">
          <Selo tom={tomSituacao}>{badge.label}</Selo>
          {/* Contemplado (estado de venda, da base de produção) — dourado brilhante. */}
          {sale.contemplado && (
            <span
              className="lance-shine inline-block rounded-[4px] border border-[#EFC563] bg-[#FBE3A6] px-2 py-0.5 text-[10.5px] font-semibold text-[#8A4F00]"
              title={sale.data_contemplacao ? `Contemplado em ${formatDate(sale.data_contemplacao)}` : 'Cota contemplada'}
            >
              Contemplado
            </span>
          )}
          {/* Inadimplente — independente do estado da venda. */}
          {sale.is_inadimplente && (
            <Selo tom="erro" title="Cliente inadimplente (parcelas em atraso)" className="bg-orange-100 text-orange-800">
              Inadimplente
            </Selo>
          )}
          {/* Lance só faz sentido em venda confirmada e NÃO contemplada
              (cota contemplada não recebe mais lance). */}
          {sale.status === 'confirmed' && !sale.contemplado && (
            <LanceStatusBadge
              status={sale.next_lance_status}
              tooltip={sale.next_lance_error}
              onClick={() => navigate(`/lances?q=${encodeURIComponent(sale.contrato || sale.cota)}`)}
            />
          )}
        </div>
        {daysOverdue > 0 && (
          <p className="mt-1 text-xs font-bold text-erro">
            Atrasado <span>{daysOverdue} dias</span>
          </p>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground">
          Criado: {formatBrDateTime(sale.created_at)}
          {isPendingSale && pendingDays > 0 ? ` · ${pendingDays.toFixed(1)} dias pendentes` : ''}
        </p>
      </div>
    </div>
  );
};
